import React from 'react';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Figure } from '@/components/ui/Figure';
import { TradeIdLink } from '@/features/trades/components/TradeIdLink';
import { formatDecimalString } from '@/utils/decimal-display';
import { columnDp, MIN_DP } from '../utils/column-dp';
import { isDirectional, prMark } from '../utils/pr-mark';
import {
  blockKey,
  findGroups,
  flattenStatement,
  searchStart,
  stickyContext,
  type StatementItem,
} from '../utils/statement-items';
import { DocumentSearch } from './DocumentSearch';
import { NO_TRADE_ORIGIN } from '@/features/trades/trade-detail-search';
import type {
  CurrencyBlock,
  PositionRef,
  PositionSectionRows,
  PreviewPositionGroup,
  PreviewRow,
  PreviewSection,
  StatementDocument,
} from '@/types';

/**
 * Fetches one position's block of daily lines, for a document that arrives
 * footed but without them (LR-571). Called once per group, when it is opened.
 *
 * Returns the block, not a bare row array: it carries the subtotal and count of
 * exactly the lines it brought, so the table can foot the rows it is printing
 * even when the document's own figure was computed minutes earlier. ``null``
 * when the position has no lines in that section - which contradicts the footing
 * that offered the expansion, so the caller says so rather than drawing an empty
 * ledger under a non-zero subtotal.
 */
export type LoadRows = (
  section: PreviewSection,
  group: PreviewPositionGroup,
) => Promise<PositionSectionRows | null>;

interface Props {
  document: StatementDocument;
  /**
   * Per-block extras (LR-557): the document page injects its Mark paid strip
   * and note editor here, keeping this renderer view-only and shared verbatim
   * with the month-to-date preview (AC: preview and detail render through one
   * component).
   */
  renderBlockExtras?: (block: CurrencyBlock) => React.ReactNode;
  /**
   * Supplies a position's daily lines on expand (LR-571). Omit it and the groups
   * render as footings only - which is all a payload that already carries its
   * own rows needs.
   */
  loadRows?: (block: CurrencyBlock) => LoadRows;
  /**
   * The element the document scrolls in. Given, only the part of the document
   * in view is mounted; `null` while the host's ref has not attached yet.
   * Omitted, the whole document renders - fine for a fixture or a small host.
   */
  scrollElement?: HTMLElement | null;
  /**
   * Where the document's search field goes: a box the host keeps outside the
   * scroller, so the field stays put while the document scrolls to a match.
   * Only with `scrollElement`: a document drawn whole is the browser's Cmd+F to
   * search.
   */
  searchSlot?: HTMLElement | null;
}

interface Column {
  label: string;
  align: 'left' | 'right';
  width: string;
  render: (row: PreviewRow, dp: number) => React.ReactNode;
  /** Stored precision - the most fraction digits this column may ever print. */
  maxDp?: number;
  /** The column's value, for measuring how many of those digits are significant. */
  get?: (row: PreviewRow) => string | null;
  /** Accessible name for the cell when the rendered content would compute a
   * worse one (see `Qty`, whose estimated-ness asterisk is otherwise silent). */
  aria?: (row: PreviewRow, dp: number) => string;
  /** Wrap the cell instead of truncating it - for a column whose text has no
   * bound and must stay readable without a mouse (see `Action`). */
  wrap?: boolean;
}

/**
 * A money/rate column. Renders through `Figure`, the shared number-priority
 * primitive (DESIGN-SPEC §2), which formats off the backend Decimal STRING via
 * `formatDecimalParts` - never Number().
 *
 * Decimals are NOT demoted inside the ledger - demotion pays off on 2dp money
 * figures (the footings), not on a rate whose fraction carries the meaning.
 */
const figureColumn = (
  label: string,
  width: string,
  get: (row: PreviewRow) => string | null,
  maxDp = 2,
): Column => ({
  label,
  align: 'right',
  width,
  maxDp,
  get,
  render: (r, dp) => (
    <Figure value={get(r)} dp={dp} tier="inherit" demoteDecimals={false} />
  ),
  aria: (r, dp) => formatDecimalString(get(r), { dp }),
});

// The ledger's columns: Date, Qty, Price, Gross, Cash Coll., Benchmark %,
// Spread %, Rate %, Fee Income, Coll. Rate %, Cash Pool Int., Action, P/R, Amount.
// There is NO Days column - product cancelled it with the business-day fold on
// 2026-07-21, so a weekend is its own line duplicating the Friday before it.
// Action (LR-554, LR-632) shows the day's events - the trade's first line, a
// re-mark, a settled recall, an applied re-rate, or maturity/termination - so a
// trader sees why the line's quantity / price / rate moved.
//
// Every width is at least what its HEADER needs on one line: the header does not
// wrap (`whitespace-nowrap` below), and the table is `table-fixed`, so a label
// wider than its column would overdraw the next one. Measure a changed label at
// the header style (Inter 11px semibold, uppercase, 0.03em tracking, px-3); the
// table already scrolls sideways, so a few pixels of width cost nothing and a
// wrapped header costs a row of height.
const buildColumns = (directional: boolean): Column[] => [
  // `tabular-nums`, and a column sized for it: Inter's proportional digits make
  // '1' narrower than the rest, so at 92px `2026-08-01` fitted while
  // `2026-08-02` overflowed by a pixel and every other day of the month came out
  // as `2026-08-...`. A date is fixed-length and carries no filler, so it is the
  // one left column that must never be shortened.
  {
    label: 'Date',
    align: 'left',
    width: '104px',
    render: (r) => <span className="tabular-nums">{r.accrual_date}</span>,
  },
  {
    label: 'Qty',
    align: 'right',
    width: '84px',
    // The asterisk is the only thing distinguishing a snapshotted size from one
    // reconstructed off the live position, and `title` on a non-focusable span
    // reaches neither the keyboard nor a screen reader (WCAG 2.1.1 / 1.3.1).
    aria: (r) =>
      r.quantity == null
        ? 'not available'
        : `${r.quantity.toLocaleString('en-US')}${
            r.quantity_is_estimated ? ', estimated from the current position' : ''
          }`,
    render: (r) =>
      r.quantity == null ? (
        '—'
      ) : (
        <>
          {r.quantity.toLocaleString('en-US')}
          {r.quantity_is_estimated && (
            <span
              className="ml-0.5 text-lr-warn"
              title="Reconstructed from the current position: this row predates the per-day size snapshot."
            >
              *
            </span>
          )}
        </>
      ),
  },
  // LR-632: the line reads left to right as one calculation - Price x Qty =
  // Gross, Gross x Margin = Cash Collateral. This replaces the single `CV`
  // column, which held whichever base the leg accrued on and so meant different
  // things on adjacent rows of one statement (LR-555). The margin itself is a
  // per-trade constant and is stated once, on the group heading above.
  // No `?? contract_value` fallback for a pre-LR-632 payload: that base is ONE of
  // the two, and printed under both it would make the other wrong by the margin.
  // A blank cell for the length of a deploy is honest; a wrong figure is not.
  figureColumn('Price', '96px', (r) => r.price, 8),
  // Sized for the FIGURES, not just the header: adjacent money columns under
  // `table-fixed`, and a nine-digit value ('12,500,000.00') is ~90px of tabular
  // digits plus 24px of padding - at 104px it would print over its neighbour.
  figureColumn('Gross', '128px', (r) => r.gross_value),
  figureColumn('Cash Coll.', '128px', (r) => r.cash_collateral),
  figureColumn('Benchmark %', '116px', (r) => r.benchmark_rate, 6),
  // 'Spread %' is the name product asked for (feedback 2026-08-20 item 9).
  // LR-618 had called it 'Spread / Fee' because a fee trade printed its fee
  // there; since LR-632 a fee trade prints it under Rate, as the reference
  // layout does, and the backend blanks this cell on every fee row - so the
  // column holds spreads and nothing else. The payload key keeps its old name.
  figureColumn('Spread %', '92px', (r) => r.spread_or_fee, 6),
  // '%' in the label: rate is a percent by the LR-346 convention (5.30 = 5.30%),
  // and a bare '5.30' beside a contract value reads as a fraction otherwise.
  {
    label: 'Rate %',
    align: 'right',
    width: '84px',
    maxDp: 6,
    get: (r) => r.rate,
    // The dagger marks the CELL, not the group: after the LR-618 round-2 sign
    // screen a mixed group is ordinary, and a derived cell can be byte-identical
    // to a booked one two rows away ('9.700000' both) - no group-level sentence
    // can tell a reader which is which (dp, Medium-4). Same pattern as Qty's
    // estimated '*', dagger because '*' is taken and '* then †' is the standard
    // footnote sequence; the CSV/PDF print the identical mark.
    aria: (r, dp) =>
      `${formatDecimalString(r.rate, { dp })}${
        r.rate_is_blended ? ', derived from the line amount' : ''
      }`,
    render: (r, dp) => (
      <>
        <Figure value={r.rate} dp={dp} tier="inherit" demoteDecimals={false} />
        {r.rate_is_blended && (
          <span
            className="ml-0.5 text-lr-warn"
            title="Derived from the line amount: no booked rate reproduces this line."
          >
            †
          </span>
        )}
      </>
    ),
  },
  // LR-632: what the Amount is made of on a fee-mode day. Both halves are signed
  // the way Amount is, so they ADD to it - a reader checks a sum, not a
  // subtraction. Blank on a single-flow row (a rebate-mode or GMRA line is one
  // amount and the Amount column already is it) and on a row that does not
  // prove its halves add up. The cost is named as the trade form, the blotter
  // and the P&L name it (LR-631): Cash Pool Interest Accrual.
  figureColumn('Fee Income', '104px', (r) => r.fee_income),
  figureColumn('Coll. Rate %', '108px', (r) => r.collateral_rate, 6),
  figureColumn('Cash Pool Int.', '124px', (r) => r.cost_of_collateral),
  {
    // LR-554: free-text day events the backend attaches to this line; blank on an
    // ordinary day. Left-aligned like Date — it is prose, not a figure. A multi-event
    // day joins with '; ' and outgrows 150px, so this cell WRAPS to a second line
    // rather than truncating: a `title` tooltip reveals cut-off text to a mouse only,
    // which leaves a keyboard (and touch) trader unable to read it — the same
    // WCAG 2.1.1 rule the Qty column above states. Wrapping costs a little row
    // density on a busy day and nothing on an ordinary one.
    label: 'Action',
    align: 'left',
    width: '150px',
    wrap: true,
    render: (r) => r.action ?? '',
  },
  // LR-632: which way the amount beside it flows, in the counterparty's terms.
  // The figure itself keeps our sign (one convention across LendRail, Tim
  // 2026-09-16); the letter is what names the direction. Blank on a document
  // with no direction in its rows, exactly as in the file.
  {
    label: 'P/R',
    align: 'left',
    width: '48px',
    render: (r) => prMark(directional ? (r.amount ?? r.net_amount) : null),
  },
  // LR-619 (feedback #7): the backend-allocated cent figure, rendered verbatim.
  // NOT `net_amount` at dp=2 — this renderer rounds half-up while the allocator
  // rounds half-even and then hands out the residual, so re-rounding here would
  // put a column on screen that does not sum to the subtotal beneath it.
  // `net_amount` is the rolling-deploy fallback: a payload built before LR-619
  // carries no `amount`, and a stale cent beats an empty column.
  figureColumn('Amount', '104px', (r) => r.amount ?? r.net_amount),
];

// Wire statuses -> display. `preview` is what the MTD document carries before
// anything is persisted. Fallback: print the raw status rather than nothing.
const STATUS_LABELS: Record<string, string> = {
  preview: 'Month-to-date (preview)',
  draft: 'Draft',
  // No app action sets it (LR-681), but an admin edit can; don't print it raw.
  issued: 'Issued',
  paid: 'Paid',
};

const AGREEMENT_LABELS: Record<string, string> = {
  gmsla: 'GMSLA',
  gmra: 'GMRA',
};

/** Statement-header label: the uppercase micro-caption the document design uses
 * for every field name (10.5px / 600 / .05em, muted). */
const HeaderLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <dt className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-lr-muted">
    {children}
  </dt>
);

/** Status / agreement chip. */
const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-lr-accent-weak px-2 py-px text-[11px] font-semibold text-lr-accent">
    {children}
  </span>
);

/**
 * LR-632: the trade's reference block, as the document's own second line under a
 * position heading.
 *
 * A SEPARATE line rather than more chips inside the disclosure button, for two
 * reasons the file already states elsewhere: text inside the button joins its
 * accessible name (a screen reader would announce the whole sentence as part of
 * "AAPL.NASDAQ … 31 lines"), and the heading is already measured at ~700px of
 * ~968px in its worst case, so six more items would ellipsise the identity away.
 *
 * The cross-currency flag prints the two currencies rather than a bare marker
 * with a `title`: a tooltip on a non-focusable span reaches neither the keyboard
 * nor a screen reader (WCAG 2.1.1), which is the same rule the Qty column states.
 * Margin is the RATIO, as the file and the reference layout print it - the screen
 * and the download are one document and must not disagree about a figure.
 */
const referenceItems = (ref: PositionRef | undefined): React.ReactNode[] => {
  // Guarded although the type says it is always there, for the reason its
  // neighbours are: new in this release, so during a rolling deploy a new bundle
  // can be handed a document built by a backend pod that has not rolled yet.
  if (ref == null) return [];
  const items: React.ReactNode[] = [];
  if (ref.description) items.push(ref.description);
  if (ref.sedol) items.push(`SEDOL ${ref.sedol}`);
  // The letter the legend below explains, in the file's own form: without it the
  // screen would define a B/L it never prints.
  if (ref.side) items.push(`B/L ${ref.side}`);
  if (ref.trade_date) items.push(`Traded ${ref.trade_date}`);
  if (ref.collateral) items.push(ref.collateral);
  if (ref.margin) items.push(`Margin ${ref.margin}`);
  if (ref.is_cross_currency) {
    items.push(
      <span key="fx" className="font-semibold text-lr-warn">
        FX {ref.currency}/{ref.settlement_currency}
      </span>,
    );
  }
  return items;
};

/** Currency-block heading: what this part of the document is denominated in,
 * under which agreement, for which account. */
const BlockHeading: React.FC<{ block: CurrencyBlock; className?: string }> = ({
  block,
  className = '',
}) => (
  <div
    className={`flex items-center justify-between gap-4 rounded-[8px] bg-lr-surface-2 px-4 py-2.5 ${className}`}
  >
    <div className="flex items-center gap-2.5">
      <span className="font-mono text-[14px] font-bold text-lr-fg">
        {block.currency}
      </span>
      <Pill>{AGREEMENT_LABELS[block.agreement] ?? block.agreement}</Pill>
      {block.account && (
        <span className="font-mono text-[11px] text-lr-faint">
          {block.account}
        </span>
      )}
      <Pill>{STATUS_LABELS[block.status] ?? block.status}</Pill>
    </div>
    <Figure
      value={block.total}
      currency={block.currency}
      softDecimals
      className="font-semibold"
    />
  </div>
);

/** Closing figure of a block, boxed and right-aligned as on an invoice. */
const BlockTotal: React.FC<{ block: CurrencyBlock }> = ({ block }) => (
  <div className="flex justify-end">
    <div className="flex w-[394px] items-baseline justify-between rounded-[10px] border border-lr-border bg-lr-surface-2 px-4 py-3">
      <span className="text-[11px] font-bold uppercase tracking-[0.05em] text-lr-muted">
        Total {block.currency}
      </span>
      <Figure
        value={block.total}
        currency={block.currency}
        tier="section"
        softDecimals
        className="font-bold"
      />
    </div>
  </div>
);

/** One statement section's heading (Fees / Rebates / Cash Pool / Repo
 * Interest) with its subtotal. */
const SectionTitle: React.FC<{ section: PreviewSection; currency: string }> = ({
  section,
  currency,
}) => (
  <div className="mb-2 flex items-baseline justify-between gap-4 border-b border-lr-border pb-1.5">
    <div>
      <h4 className="text-[12px] font-bold uppercase tracking-[0.05em] text-lr-fg">
        {section.title}
      </h4>
      {section.subtitle && (
        <p className="text-[11px] text-lr-faint">{section.subtitle}</p>
      )}
    </div>
    <span className="whitespace-nowrap text-[12px] text-lr-muted">
      Subtotal{' '}
      {/* LR-632: the document's allocated cent (`amount`) - the figure the
          summary above and the file print. Rounding the 8dp `subtotal` here
          (half-up, no residual) could land a cent away from both. */}
      <Figure
        value={section.amount ?? section.subtotal}
        currency={currency}
        softDecimals
        className="ml-0.5 font-semibold text-lr-fg"
      />
    </span>
  </div>
);

/** A section's heading, and for an empty section its placeholder: it stays
 * visible as structure - cash pool exists on the document before it has
 * entries, and hiding it would make its later appearance look like a change. */
const SectionHeading: React.FC<{ section: PreviewSection; currency: string }> = ({
  section,
  currency,
}) => (
  <>
    <SectionTitle section={section} currency={currency} />
    {section.is_empty && (
      <div className="rounded-[8px] border border-dashed border-lr-border-2 px-4 py-5 text-center text-[12px] text-lr-faint">
        No entries this period.
      </div>
    )}
  </>
);

/** The daily-lines table of one position, once they are in hand.
 *
 * Foots on the block's OWN subtotal, not the document's: the two are computed by
 * different requests minutes apart (LR-571), and a subtotal printed under lines
 * it does not add up to is exactly what a reader of an invoice would catch. The
 * document's figure stays on the collapsed header, where it ties to the section
 * and the invoice.
 */
const LedgerTable: React.FC<{
  id: string;
  section: PreviewSection;
  group: PreviewPositionGroup;
  block: PositionSectionRows;
  currency: string;
  directional: boolean;
}> = ({ id, section, group, block, currency, directional }) => {
  const rows = block.rows;
  const columns = React.useMemo(
    () => buildColumns(directional),
    [directional],
  );
  // The header above was footed by the document request; these lines came from a
  // later one. On the month-to-date preview the accrual job can book another day
  // in between, and then one security carries two different subtotals on screen
  // at once. Both figures are correct for their own moment, so neither is
  // dropped - but a reader of an invoice must be told which is which rather than
  // left to notice.
  const drifted =
    block.subtotal !== group.subtotal || block.row_count !== group.row_count;
  // Per column, per position block: the fraction width that fits this block's
  // own figures, so one position's long rate does not pad every other
  // position's round ones back out to six zeros.
  const dps = columns.map((c) =>
    c.maxDp == null || c.get == null
      ? MIN_DP
      : columnDp(
          rows.map(c.get),
          c.maxDp,
        ),
  );

  return (
    <div id={id}>
      {drifted && (
        <p
          role="status"
          className="mb-1 rounded-[6px] bg-lr-surface-2 px-3 py-1.5 text-[11px] text-lr-warn"
        >
          {/* Both figures are named, neither is pointed at. It used to say
              "against the … above", which stopped being true the moment the
              heading started following the table (LR-619 HIGH-1): the heading
              then shows the LINES' total, so "above" pointed at a figure that
              is not on the screen, and the banner exists precisely so the
              reader is not left to work out which is which (dp, review, M1).
              The lines' figure is the allocated one - the cents a reader can
              actually add up. */}
          These lines were accrued after this document was footed: they total{' '}
          <Figure
            value={block.amount_total ?? block.subtotal}
            currency={currency}
            softDecimals
          />
          , while the document was footed at{' '}
          <Figure
            value={group.subtotal}
            currency={currency}
            softDecimals
          />.
          Reopen the preview to re-foot it.
        </p>
      )}
      <div className="scroll-x-visible overflow-x-auto rounded-[8px] border border-lr-border">
      <table className="w-full table-fixed text-[13px]">
        <caption className="sr-only">
          {section.title}: accrual entries for {group.symbol_id} (TRD-
          {group.position_id})
        </caption>
        <colgroup>
          {columns.map((c) => (
            <col key={c.label} style={{ width: c.width }} />
          ))}
        </colgroup>
        <thead className="bg-lr-surface-2">
          <tr className="border-b border-lr-border">
            {columns.map((c) => (
              <th
                key={c.label}
                scope="col"
                className={`whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.03em] text-lr-muted ${
                  c.align === 'right' ? 'text-right' : 'text-left'
                }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-lr-surface">
          {rows.map((r, i) => (
            <tr
              key={`${r.accrual_date}-${i}`}
              className="border-b border-lr-border-soft last:border-b-0 hover:bg-lr-row-active"
            >
              {columns.map((c, ci) => (
                <td
                  key={c.label}
                  aria-label={c.aria?.(r, dps[ci])}
                  className={`px-3 py-[7px] ${
                    c.align === 'right'
                      ? 'text-right'
                      : `text-lr-fg-2 ${c.wrap ? 'break-words' : 'truncate'}`
                  }`}
                >
                  {c.render(r, dps[ci])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-lr-border bg-lr-surface-2">
            <td
              colSpan={columns.length - 1}
              className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.03em] text-lr-muted"
            >
              Subtotal
            </td>
            <td className="px-3 py-2 text-right">
              {/* The ALLOCATED footing (LR-619), not `subtotal` re-rounded here.
                  The lines above print backend-allocated cents summed half-even;
                  this renderer rounds half-up, so on a half-cent block the two
                  disagree — 0.34 + 0.33 + 0.33 under a printed 1.01. Falls back
                  to `subtotal` for a payload that predates the field. */}
              <Figure
                value={block.amount_total ?? block.subtotal}
                currency={currency}
                softDecimals
                className="font-semibold"
              />
            </td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
};

type GroupItem = Extract<StatementItem, { kind: 'group' }>;

interface GroupState {
  open: boolean;
  block: PositionSectionRows | null;
  loading: boolean;
  error: string | null;
}

/** A group's state, with the footing it was reached under (see `groupState`). */
type StoredGroupState = GroupState & { footing: string };

const footingOf = (group: PreviewPositionGroup) =>
  `${group.row_count}|${group.subtotal}|${group.last_date}`;

// One object per group, so a memoized group that nobody has touched keeps an
// identical `state` prop across renders.
const initialStates = new WeakMap<PreviewPositionGroup, GroupState>();

/**
 * The state a group starts in. Rows already in hand are shown: there is nothing
 * to fetch and no reason to make a reader click for what was already sent. Only
 * a group whose lines have to be fetched starts closed - which, since LR-571, is
 * every group the document endpoints return.
 */
const initialState = ({ section, group }: GroupItem): GroupState => {
  let state = initialStates.get(group);
  if (state === undefined) {
    const inline: PositionSectionRows | null =
      group.rows.length > 0
        ? {
            key: section.key,
            title: section.title,
            subtotal: group.subtotal,
            // The document's allocated cent where the payload has one (LR-632),
            // otherwise null - never a locally-quantized `subtotal`: allocation
            // is the backend's, and a cent figure computed here would be the FE
            // doing money math on its own (HON-03) - half-up where the allocator
            // is half-even, with no residual handed out. The rows carry whatever
            // `amount` the payload gave them, and the column falls back per row.
            amount_total: group.amount ?? null,
            row_count: group.rows.length,
            rows: group.rows,
          }
        : null;
    state = { open: inline !== null, block: inline, loading: false, error: null };
    initialStates.set(group, state);
  }
  return state;
};

/**
 * What a group shows. Kept by the document rather than the group, because the
 * group unmounts when it scrolls out of view and must come back as it was left.
 *
 * Stored state counts only while the group's footing is the one it was reached
 * under. A refetched document re-foots the group (the month-to-date preview
 * accrues another day while the modal is open), and lines fetched for the old
 * figure must not be left sitting under the new one - so they are dropped and
 * the next open asks again. The disclosure closes with them: an open group with
 * no panel would leave `aria-controls` pointing at nothing and read as an empty
 * ledger. Keyed on the figures, not on the `group` object, which is a fresh
 * reference on every refetch.
 */
const groupState = (
  stored: StoredGroupState | undefined,
  item: GroupItem,
): GroupState =>
  stored !== undefined && stored.footing === footingOf(item.group)
    ? stored
    : initialState(item);

/**
 * One position's block: its footing, and its daily lines behind a disclosure.
 *
 * The document arrives footed but WITHOUT rows (LR-571) - a large counterparty's
 * month runs to hundreds of thousands of daily lines, which neither the response
 * nor the DOM can carry - so the lines are fetched for one position when it is
 * opened. A payload that carries its own rows renders them without asking for
 * anything; no endpoint produces that shape today, but this renderer is fed
 * fixtures as well as responses and stays honest about what it was given.
 *
 * Children are spaced by `gap`, not margins, so nothing leaks past the group's
 * own box: the virtualizer measures that box, and a margin collapsing out of it
 * would put every later group a few pixels off where it is drawn.
 */
const PositionGroupView = React.memo(function PositionGroupView({
  item,
  state,
  directional,
  expandable,
  found,
  onToggle,
}: {
  item: GroupItem;
  state: GroupState;
  directional: boolean;
  /** The search's current match: ringed, which leaves the layout as measured
   * (its reach is `FOUND_RING_REACH`). Not tinted: on any tint the faint ISIN
   * and reference text fall below AA. */
  found: boolean;
  /** Nothing to disclose without a way to get the lines: a group with neither
   * inline rows nor a loader stays a footing, rather than an affordance that
   * opens onto nothing. */
  expandable: boolean;
  onToggle: (item: GroupItem) => void;
}) {
  const { section, group, securityTotal } = item;
  const { currency } = item.block;
  const { open, block, loading, error } = state;
  const panelId = `ledger-${section.key}-${group.position_id}`;

  // Guarded although the type says these are always present, and for the same
  // reason its twin in `BillingStatementExpandedRow` guards them: they are new in
  // LR-571, so during a rolling deploy a new bundle can be handed a document
  // built by a backend pod that has not rolled yet. That payload carries `rows`
  // instead, which this component still renders - it must not then die on its
  // own header. `/billing` has no route-level ErrorBoundary, so a throw here
  // would blank the statements list, not just the preview.
  const lineCount = group.row_count ?? null;
  // LR-618: this position's Rate cells are a netted blend, not a contracted
  // rate. Announced once here rather than on every line, because it is a
  // property of the trade's shape and holds for the whole group. Only knowable
  // once the lines are in hand — the footed document carries no rows (LR-571) —
  // so it appears on expansion, which is also the only time the Rate column is
  // on screen to be explained.
  // `=== true` rather than a bare truthiness check, and for the same reason
  // `row_count` is guarded above: the field is new in this release, so during a
  // rolling deploy a new bundle can be handed rows built by a backend pod that
  // has not rolled yet. Absent means "not known to be blended", which is the
  // safe reading — the note is an explanation, and inventing one is worse than
  // omitting it for the length of a deploy.
  const blended = block?.rows.some((r) => r.rate_is_blended === true) ?? false;
  const reference = referenceItems(group.ref);
  const dates =
    group.first_date == null || group.last_date == null
      ? null
      : group.first_date === group.last_date
        ? group.first_date
        : `${group.first_date} – ${group.last_date}`;

  return (
    <>
      <div
        className={`flex flex-col gap-1.5 ${
          found
            ? 'rounded-[2px] ring-2 ring-lr-accent ring-offset-4 ring-offset-lr-surface'
            : ''
        }`}
      >
        <div className="flex items-baseline justify-between gap-4">
          <h5 className="flex min-w-0 items-baseline gap-2">
            <button
              type="button"
              onClick={expandable ? () => onToggle(item) : undefined}
              disabled={!expandable}
              aria-expanded={expandable ? open : undefined}
              aria-controls={open ? panelId : undefined}
              aria-current={found || undefined}
              className={`flex min-w-0 items-baseline gap-2 rounded text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lr-accent ${
                expandable ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              {expandable &&
                (open ? (
                  <ChevronDownIcon
                    className="h-3.5 w-3.5 shrink-0 self-center text-lr-muted"
                    aria-hidden="true"
                  />
                ) : (
                  <ChevronRightIcon
                    className="h-3.5 w-3.5 shrink-0 self-center text-lr-muted"
                    aria-hidden="true"
                  />
                ))}
              <span className="shrink-0 font-mono text-[13px] font-bold text-lr-fg">
                {group.symbol_id}
              </span>
              {group.isin && (
                <span className="shrink-0 font-mono text-[11px] text-lr-faint">
                  {group.isin}
                </span>
              )}
              {group.trade_type && (
                <span className="shrink-0 text-[11px] uppercase tracking-[0.03em] text-lr-muted">
                  {group.trade_type}
                </span>
              )}
              {/* What opening this would show, so the click is an informed one.
                  The only shrinkable item in the heading (everything else is
                  `shrink-0`), so a heading too wide for its host ellipsises HERE
                  and the identity - ticker, ISIN, type, TRD - survives intact.
                  Today nothing overflows: ~700px of worst-case heading against
                  ~968px of content on both hosts (dp review). */}
              {lineCount != null && (
                <span className="truncate text-[11px] text-lr-faint">
                  {lineCount.toLocaleString('en-US')}{' '}
                  {lineCount === 1 ? 'line' : 'lines'}
                  {dates != null && ` · ${dates}`}
                </span>
              )}
            </button>
            {/* LR-613: the trade behind this group, for trace/reconciliation. A
                SIBLING of the disclosure button — a link inside it would nest two
                interactive elements, which is invalid HTML. TRD- prefix: this is
                an identity surface, the detail-page convention, not a table cell. */}
            <span className="shrink-0 whitespace-nowrap font-mono text-[11px]">
              <TradeIdLink
                id={group.position_id}
                origin={NO_TRADE_ORIGIN}
                prefix="TRD-"
              />
            </span>
          </h5>
          <span className="whitespace-nowrap text-[12px] text-lr-muted">
            Subtotal{' '}
            {/* Once the lines are in hand, the heading shows the same allocated
                figure the table foots on - two contradictory subtotals a hand's
                breadth apart is worse than one. Collapsed it is the document's
                own allocated cent (`amount`, LR-632), which is what the section
                and security totals around it add up from. */}
            <Figure
              value={block?.amount_total ?? group.amount ?? group.subtotal}
              currency={currency}
              softDecimals
              className="ml-0.5 font-semibold text-lr-fg"
            />
          </span>
        </div>

        {/* LR-632: what this trade IS - security name, SEDOL, trade date,
            collateral, margin, and the cross-currency flag. See `referenceItems`
            for why it is its own line rather than more chips in the heading. */}
        {reference.length > 0 && (
          <p className="flex flex-wrap items-baseline gap-x-1.5 text-[11px] text-lr-faint">
            {reference.map((ref, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span aria-hidden="true">·</span>}
                <span>{ref}</span>
              </React.Fragment>
            ))}
          </p>
        )}

        {/* Deliberately OUTSIDE the disclosure button: inside, it joins the
            button's accessible name, so a screen reader announces the whole
            sentence as part of "AAPL.NASDAQ … 31 lines". It explains the table
            below, not the control, so it sits between them.

            Since round 2 the sentence is the LEGEND for the per-cell dagger, not
            a claim about the whole group - a mixed group (booked and derived
            lines side by side) is the ordinary case, and the old wording was
            false on its booked lines (dp, Medium-4). Text kept verbatim equal to
            the export's `_BLENDED_RATE_NOTE`, same as before. */}
        {blended && (
          <p className="text-[11px] text-lr-muted">
            † Rate derived from the line amount: no booked rate reproduces this
            line (a netted fee/benchmark day, or an accrual basis that has since
            changed).
          </p>
        )}

        {/* One live region that persists across the whole disclosure. `role="status"`
            announces a CHANGE OF CONTENT inside a region, so a status box that is
            unmounted when the table replaces it announces the wait and then nothing
            - the reader is never told the lines arrived. Keeping the region mounted
            and swapping its text makes the arrival an update, which is announced.
            The error is announced here too, not by a `role="alert"` panel: the
            group remounts each time it scrolls back into the window, and an
            alert is announced on every insertion. The region goes with the group,
            so lines that arrive while it is scrolled away are not announced. */}
        <div role="status" className="sr-only">
          {open && loading
            ? 'Loading daily lines…'
            : open && error
              ? error
              : open && block
                ? `${block.row_count.toLocaleString('en-US')} daily lines loaded.`
                : ''}
        </div>

        {/* Every open state carries the panel id: `aria-controls` must point at an
            element that is actually in the DOM, and while the lines are in flight
            the panel is this box rather than the table. */}
        {open && loading && (
          <div
            id={panelId}
            className="rounded-[8px] border border-lr-border px-4 py-3 text-[12px] text-lr-muted"
          >
            Loading daily lines…
          </div>
        )}
        {open && error && (
          <div
            id={panelId}
            className="rounded-[8px] border border-lr-border px-4 py-3 text-[12px] text-lr-neg"
          >
            {error}
          </div>
        )}
        {open && !loading && !error && block && (
          <LedgerTable
            id={panelId}
            section={section}
            group={group}
            block={block}
            currency={currency}
            directional={directional}
          />
        )}
      </div>

      {/* LR-632: a security lent on several trades is footed once beneath them. */}
      {securityTotal && (
        <p className="mt-5 flex items-baseline justify-between gap-4 border-t border-lr-border pt-1.5 text-[12px] text-lr-muted">
          <span>
            Total{' '}
            <span className="font-mono font-semibold text-lr-fg">
              {securityTotal.symbol_id}
            </span>{' '}
            · {securityTotal.trade_count} trades
          </span>
          <Figure
            value={securityTotal.amount ?? securityTotal.subtotal}
            currency={currency}
            softDecimals
            className="font-semibold text-lr-fg"
          />
        </p>
      )}
    </>
  );
});

/**
 * One item of the document's list. `flow-root` keeps its children's margins
 * inside its box, which is what the virtualizer measures; the spacing between
 * items is padding for the same reason.
 *
 * When the focused control scrolls out of the window and unmounts, focus would
 * fall to `<body>` - arrow keys stop scrolling the document and Tab restarts
 * from the top of the page. `onFocusLost` hands it to the list instead. A
 * layout-effect cleanup runs while the node is still attached, so it can still
 * tell whether focus is inside.
 */
const ListItem: React.FC<{
  index: number;
  className: string;
  measure?: (node: HTMLDivElement | null) => void;
  onFocusLost?: () => void;
  children: React.ReactNode;
}> = ({ index, className, measure, onFocusLost, children }) => {
  const node = React.useRef<HTMLDivElement | null>(null);
  const ref = React.useCallback(
    (el: HTMLDivElement | null) => {
      node.current = el;
      measure?.(el);
    },
    [measure],
  );
  React.useLayoutEffect(
    () => () => {
      if (node.current?.contains(globalThis.document.activeElement)) onFocusLost?.();
    },
    [onFocusLost],
  );
  return (
    <div ref={ref} data-index={index} className={`flow-root ${className}`}>
      {children}
    </div>
  );
};

/** Top padding of an item: the spacing the nested layout had between blocks,
 * sections and groups, moved onto the item that follows it. */
const itemSpacing = (item: StatementItem): string => {
  switch (item.kind) {
    case 'block-head':
      return item.blockIndex > 0 ? 'pt-8' : '';
    case 'block-total':
      return 'pt-4';
    case 'section-head':
      return item.first ? '' : 'pt-6';
    case 'group':
      return item.first ? '' : 'pt-5';
  }
};

// First-guess heights, replaced by measurement as each item is drawn.
const ESTIMATED_HEIGHT: Record<StatementItem['kind'], number> = {
  'block-head': 60,
  'section-head': 48,
  group: 64,
  'block-total': 64,
};

// Items drawn while the scroll container reports no height - hidden, or jsdom.
// The virtualizer finds no range then and would draw nothing at all.
const FALLBACK_ITEMS = 40;

// How far the ring around the search's current match reaches past its group
// (`ring-2` + `ring-offset-4`), plus a little air.
const FOUND_RING_REACH = 10;

const sectionHeadOf = (item: StatementItem) =>
  'sectionHead' in item ? item.sectionHead : -1;

/**
 * The billing document (LR-557, structure from LR-553 item 3): one document per
 * counterparty per period. Masthead carries the per-currency summary - there is
 * deliberately NO total across currencies (product decision: figures in
 * different denominations are not addable). Below it, one block per persisted
 * statement (currency + agreement + account), each with the four economic
 * sections and their subtotals, closing on the block's own authoritative total.
 *
 * View-only. Shared by the month-to-date preview and the document page, so the
 * two renderings never drift; page-only controls arrive via `renderBlockExtras`.
 *
 * Given a `scrollElement`, only the part of the document in view is mounted
 * (LR-741): a large counterparty's book is tens of thousands of position groups,
 * and drawing all of them made every render of the host take seconds. Not
 * through `VirtualizedGrid`, which owns its scroll container: here the masthead,
 * and the preview's tabs, scroll in the host's together with the document.
 */
const StatementDocumentViewImpl: React.FC<Props> = ({
  document,
  renderBlockExtras,
  loadRows,
  scrollElement,
  searchSlot,
}) => {
  const { header, blocks } = document;
  // LR-632: whether the rows carry a payment direction at all. One comparison
  // decides the P/R letters, the Payable / Receivable summary and the legend
  // below, the way the file does it (`_is_directional`).
  const directional = isDirectional(header.period_start);
  const items = React.useMemo(() => flattenStatement(document), [document]);

  const [groupStates, setGroupStates] = React.useState<
    ReadonlyMap<string, StoredGroupState>
  >(() => new Map());
  // Read by the stable `toggle` below, so that memoized groups are not
  // re-rendered by every open or close elsewhere in the document.
  const latest = React.useRef({ groupStates, loadRows });
  latest.current = { groupStates, loadRows };

  const toggle = React.useCallback(async (item: GroupItem) => {
    const footing = footingOf(item.group);
    // `settled` marks the answer to a request: it lands only while the group is
    // still in the state that asked for it. After a refetch re-foots the group
    // and it is opened again, a late answer to the old request must not close it.
    const patch = (next: Partial<GroupState>, settled = false) =>
      setGroupStates((prev) => {
        const stored = prev.get(item.key);
        if (settled && stored?.footing !== footing) return prev;
        const map = new Map(prev);
        map.set(item.key, { ...groupState(stored, item), ...next, footing });
        return map;
      });
    const { groupStates: states, loadRows: load } = latest.current;
    const current = groupState(states.get(item.key), item);
    if (current.open) {
      patch({ open: false });
      return;
    }
    // The error is cleared on every open: one left over from a failed attempt
    // would otherwise outlive it and hide the rows a later one fetched.
    // `loading` guards the second click: collapsing does not cancel the request
    // in flight, and a second one could resolve out of order.
    if (current.block !== null || current.loading || !load) {
      patch({ open: true, error: null });
      return;
    }
    patch({ open: true, error: null, loading: true });
    try {
      const loaded = await load(item.block)(item.section, item.group);
      // The footing offered lines and the ledger has none: say so rather than
      // drawing an empty table under a non-zero subtotal, which reads as "this
      // position accrued nothing".
      patch(
        loaded === null || loaded.rows.length === 0
          ? { loading: false, error: 'No daily lines came back for this position.' }
          : { loading: false, block: loaded },
        true,
      );
    } catch {
      patch({ loading: false, error: 'Could not load the daily lines.' }, true);
    }
  }, []);

  const mastheadRef = React.useRef<HTMLDListElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const virtualized = scrollElement !== undefined;

  // Where the list starts inside the scroll container: the masthead, and in the
  // preview the tabs, sit above it and scroll with it.
  // NOTE: re-measured on a masthead resize and a new document only. A line above
  // the list that comes or goes while the document stays (the host's error over
  // a kept document, cleared by the next refetch) shifts the list by its height
  // until then. Gone, it leaves the margin too large and the band names the
  // section above for that many pixels past a new section's heading. Upgrade
  // path: re-measure on every render - the tests then need a layout stub, as
  // jsdom's rects ignore scrolling.
  const [scrollMargin, setScrollMargin] = React.useState(0);
  React.useLayoutEffect(() => {
    const list = listRef.current;
    const masthead = mastheadRef.current;
    if (!scrollElement || !list || !masthead) return;
    const measure = () =>
      setScrollMargin(
        list.getBoundingClientRect().top -
          scrollElement.getBoundingClientRect().top +
          scrollElement.scrollTop,
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(masthead);
    return () => observer.disconnect();
  }, [scrollElement, document]);

  // A sticky box sticks inside the scroller's padding, so `top: 0` would leave a
  // padding-high strip above the repeated headings with lines showing through.
  const stickyTop = React.useMemo(
    () =>
      scrollElement
        ? -(parseFloat(getComputedStyle(scrollElement).paddingTop) || 0)
        : 0,
    [scrollElement],
  );

  // The repeated headings cover the top of the scroller. A control Shift+Tab
  // lands on under them counts as in view, so the browser would leave focus
  // hidden there (WCAG 2.4.11); scroll padding makes it scroll the control
  // clear. The deepest the band has been, not its height now: the band is
  // redrawn for where the browser scrolls to, and may grow or appear there. The
  // scroller is the host's, so this is undone.
  const stickyBand = React.useRef<HTMLDivElement>(null);
  const bandDepth = React.useRef(0);
  React.useLayoutEffect(() => {
    if (!scrollElement) return;
    bandDepth.current = Math.max(
      bandDepth.current,
      stickyBand.current?.offsetHeight ?? 0,
    );
    scrollElement.style.scrollPaddingTop = `${bandDepth.current}px`;
    return () => {
      scrollElement.style.scrollPaddingTop = '';
    };
  });

  // Stable per list: the virtualizer rebuilds its whole measurement cache, one
  // entry per item, whenever either of them changes.
  const estimateSize = React.useCallback(
    (i: number) => ESTIMATED_HEIGHT[items[i].kind],
    [items],
  );
  const getItemKey = React.useCallback((i: number) => items[i].key, [items]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElement ?? null,
    estimateSize,
    getItemKey,
    overscan: 8,
    scrollMargin,
    // A match scrolled to lands below the band, ring and all: the first group
    // of a section has no spacing above it to hold the ring. Read at render:
    // on a first jump the band is not drawn yet, and the jump clears it only
    // because the virtualizer re-reads this while it settles the jump (the
    // band test pins that).
    scrollPaddingStart: bandDepth.current + FOUND_RING_REACH,
    enabled: scrollElement != null,
  });

  // The current match is kept by its key, so a refetch that adds or drops a
  // match before it leaves the ring where it was; by number once it is gone.
  const [query, setQuery] = React.useState('');
  const [step, setStep] = React.useState(0);
  const [currentKey, setCurrentKey] = React.useState<string | null>(null);
  const matches = React.useMemo(() => findGroups(items, query), [items, query]);
  const kept = matches.findIndex((i) => items[i].key === currentKey);
  // The number follows the position, so that once it is gone the match now at
  // its number takes over, not one at the number it had before matches moved.
  if (kept >= 0 && kept !== step) setStep(kept);
  const at = kept >= 0 ? kept : Math.min(step, matches.length - 1);
  const current = at >= 0 ? items[matches[at]] : undefined;
  // Scrolled on what the reader does, never on a refetch.
  const go = (found: number[], k: number) => {
    setStep(k);
    setCurrentKey(found.length > 0 ? items[found[k]].key : null);
    if (found.length === 0) return;
    virtualizer.scrollToIndex(found[k], { align: 'start' });
  };
  const search = (value: string) => {
    setQuery(value);
    const found = findGroups(items, value);
    go(found, searchStart(items, found, value));
  };
  const stepBy = (by: 1 | -1) => {
    if (matches.length === 0) return;
    go(matches, (at + by + matches.length) % matches.length);
  };

  // The band names the headings of the first item it does not cover. Taken at
  // the scroller's top edge instead, it would go on naming the section above
  // while the next section's heading slid under it. Read at the deepest the
  // band has been rather than its current height: a band that drops its section
  // line would otherwise look higher, find the old section, regain the line,
  // and flicker. It shows only once its block's own heading has left the top
  // edge: until then that heading is on screen, and the band would be drawn
  // over it.
  const subscribeToScroll = React.useCallback(
    (onScroll: () => void) => {
      scrollElement?.addEventListener('scroll', onScroll, { passive: true });
      return () => scrollElement?.removeEventListener('scroll', onScroll);
    },
    [scrollElement],
  );
  const bandIndex = React.useSyncExternalStore(subscribeToScroll, () => {
    if (!scrollElement) return -1;
    const at = (offset: number) =>
      virtualizer.getVirtualItemForOffset(offset)?.index ?? -1;
    const below = at(scrollElement.scrollTop + bandDepth.current);
    return below >= 0 && at(scrollElement.scrollTop) > items[below].blockHead
      ? below
      : -1;
  });
  const windowed = virtualizer.getVirtualItems();
  const indices = !virtualized
    ? items.map((_, i) => i)
    : windowed.length > 0
      ? windowed.map((v) => v.index)
      : items.slice(0, FALLBACK_ITEMS).map((_, i) => i);
  const offset = windowed.length > 0 ? windowed[0].start - scrollMargin : 0;

  // Focusable only while it holds the focus it was handed: a lasting tabindex
  // would also take the focus of a click on plain text, and the next Tab would
  // then start from the top of the drawn items rather than from the click.
  // A window blur (switching apps) fires `blur` too but leaves the list active;
  // dropping the tabindex then would lose the focus on return.
  const onFocusLost = React.useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    // A tabindex still set means its `release` is still waiting: one is enough.
    if (!list.hasAttribute('tabindex')) {
      const release = () => {
        if (globalThis.document.activeElement === list) return;
        list.removeAttribute('tabindex');
        list.removeEventListener('blur', release);
      };
      list.tabIndex = -1;
      list.addEventListener('blur', release);
    }
    list.focus({ preventScroll: true });
  }, []);

  const renderItem = (index: number) => {
    const item = items[index];
    let content: React.ReactNode;
    switch (item.kind) {
      case 'block-head':
        content = (
          <>
            <BlockHeading block={item.block} className="mb-3" />
            {renderBlockExtras?.(item.block)}
          </>
        );
        break;
      case 'section-head':
        content = (
          <SectionHeading section={item.section} currency={item.block.currency} />
        );
        break;
      case 'group':
        content = (
          <PositionGroupView
            item={item}
            state={groupState(groupStates.get(item.key), item)}
            directional={directional}
            expandable={item.group.rows.length > 0 || loadRows !== undefined}
            found={item === current}
            onToggle={toggle}
          />
        );
        break;
      case 'block-total':
        content = <BlockTotal block={item.block} />;
        break;
    }
    return (
      <ListItem
        key={item.key}
        index={index}
        className={itemSpacing(item)}
        measure={virtualized ? virtualizer.measureElement : undefined}
        onFocusLost={virtualized ? onFocusLost : undefined}
      >
        {content}
      </ListItem>
    );
  };

  // The drawn items, inside the block and section landmarks they belong to - so
  // a screen reader's region list still names the currency and the section of
  // what is on screen, even when their headings have scrolled out of the window.
  const nested: React.ReactNode[] = [];
  for (let i = 0; i < indices.length; ) {
    const { block, blockIndex } = items[indices[i]];
    const inBlock: React.ReactNode[] = [];
    while (i < indices.length && items[indices[i]].blockIndex === blockIndex) {
      const first = items[indices[i]];
      const sectionHead = sectionHeadOf(first);
      if (sectionHead < 0 || !('section' in first)) {
        inBlock.push(renderItem(indices[i++]));
        continue;
      }
      const inSection: React.ReactNode[] = [];
      while (i < indices.length && sectionHeadOf(items[indices[i]]) === sectionHead) {
        inSection.push(renderItem(indices[i++]));
      }
      inBlock.push(
        <section key={items[sectionHead].key} aria-label={first.section.title}>
          {inSection}
        </section>,
      );
    }
    nested.push(
      <section
        key={blockKey(block)}
        aria-label={`${block.currency} ${AGREEMENT_LABELS[block.agreement] ?? block.agreement}`}
      >
        {inBlock}
      </section>,
    );
  }

  // The headings of whatever sits just below the band, repeated once the
  // originals have scrolled away. A visual aid only: the landmarks above already
  // name both for assistive technology.
  const sticky = virtualized ? stickyContext(items, bandIndex) : null;

  return (
    <div className="text-lr-fg">
      {virtualized &&
        searchSlot &&
        blocks.length > 0 &&
        createPortal(
          <DocumentSearch
            query={query}
            onQueryChange={search}
            current={at + 1}
            total={matches.length}
            match={
              current?.kind === 'group'
                ? `${current.group.symbol_id} TRD-${current.group.position_id}, ${current.section.title}`
                : undefined
            }
            onStep={stepBy}
          />,
          searchSlot,
        )}
      {/* Document masthead. The heavy rule under it is the invoice convention:
          it separates who/when/how-much from the ledger itself. */}
      <dl ref={mastheadRef} className="mb-6 grid grid-cols-2 gap-x-8 gap-y-2.5 border-b-2 border-lr-fg pb-4 text-[12.5px]">
        <div className="flex items-baseline justify-between gap-3">
          <HeaderLabel>Counterparty</HeaderLabel>
          <dd className="font-semibold">{header.counterparty_name}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <HeaderLabel>Period</HeaderLabel>
          <dd className="font-medium">
            {header.period_start ?? '—'} – {header.period_end ?? '—'}
          </dd>
        </div>
        <div className="col-span-2">
          <HeaderLabel>Total by currency</HeaderLabel>
          {header.currency_totals.length === 0 ? (
            <dd className="mt-1 text-[12px] text-lr-faint">
              No accruals this period.
            </dd>
          ) : directional ? (
            /* LR-632: the reference layout's Summary - each currency broken down
               by section, and each section by direction. Payable and Receivable
               are the two halves of Net, split by the sign of each TRADE's
               footing: which trades you owe on, and which you are owed on -
               named in the reader's terms, like the P/R letter. The figures keep
               our sign like every other on the page, so Payable + Receivable =
               Net reads across the row. Only on a directional-era document
               (`isDirectional`) - the words name a direction, and a pre-epoch
               document has none to name. */
            <dd className="mt-1.5 overflow-x-auto">
              <table className="text-[12px]">
                <caption className="sr-only">
                  Total by currency and section, payable and receivable
                </caption>
                <thead>
                  <tr className="text-[10.5px] uppercase tracking-[0.05em] text-lr-muted">
                    <th scope="col" className="pr-6 pb-1 text-left font-semibold">
                      Currency
                    </th>
                    <th scope="col" className="pr-6 pb-1 text-left font-semibold">
                      Section
                    </th>
                    <th scope="col" className="pr-6 pb-1 text-right font-semibold">
                      Payable
                    </th>
                    <th scope="col" className="pr-6 pb-1 text-right font-semibold">
                      Receivable
                    </th>
                    <th scope="col" className="pr-3 pb-1 text-right font-semibold">
                      Net
                    </th>
                    <th scope="col" className="pb-1 text-left font-semibold">
                      P/R
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {header.currency_totals.map((t) => {
                    // Guarded although the type says it is always there: a
                    // backend pod that has not rolled yet sends no `sections`,
                    // and `/billing` has no route-level ErrorBoundary. Such a
                    // currency prints its total alone.
                    const sections = t.sections ?? [];
                    return (
                      <React.Fragment key={t.currency}>
                        {sections.map((sec, i) => (
                          <tr key={sec.key}>
                            <td className="pr-6 font-mono text-[11px] text-lr-muted">
                              {/* Printed once per currency for the eye; a screen
                                  reader moving row by row still hears it. */}
                              {i === 0 ? (
                                t.currency
                              ) : (
                                <span className="sr-only">{t.currency}</span>
                              )}
                            </td>
                            <td className="pr-6">{sec.title}</td>
                            <td className="pr-6 text-right">
                              <Figure value={sec.due_to_us} softDecimals />
                            </td>
                            <td className="pr-6 text-right">
                              <Figure value={sec.due_to_them} softDecimals />
                            </td>
                            <td className="pr-3 text-right">
                              <Figure value={sec.net} softDecimals />
                            </td>
                            <td className="text-lr-muted">{prMark(sec.net)}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-lr-border font-semibold">
                          <td className="pr-6 font-mono text-[11px] text-lr-muted">
                            {sections.length === 0 ? t.currency : ''}
                          </td>
                          <td className="pr-6">Total {t.currency}</td>
                          <td />
                          <td />
                          <td className="pr-3 text-right">
                            <Figure
                              value={t.total}
                              currency={t.currency}
                              softDecimals
                            />
                          </td>
                          <td className="text-lr-muted">{prMark(t.total)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </dd>
          ) : (
            <dd className="mt-1 flex flex-wrap gap-x-8 gap-y-1">
              {header.currency_totals.map((t) => (
                <span key={t.currency} className="flex items-baseline gap-2">
                  <span className="font-mono text-[11px] text-lr-muted">
                    {t.currency}
                  </span>
                  <Figure
                    value={t.total}
                    currency={t.currency}
                    softDecimals
                    className="font-semibold"
                  />
                </span>
              ))}
            </dd>
          )}
        </div>
        {/* LR-618: the sign convention, stated once and inside the masthead rule
            with the rest of who/when/how-much. Needed because Rate now carries
            the rate's own sign while Amount carries the direction of payment,
            and nothing on the page said which was which. Deliberately the same
            first-person voice the section subtitles already use ("fee payable to
            Exante"). LR-632 adds the P/R key: the sign stays ours, as in the
            statements list and Mark paid (Tim, 2026-09-16), while the letter is
            the reader's, as the reference layout's own legend defines it. B/L
            is OUR side, as everywhere in LendRail, and the legend says so: the
            reference layout prints the counterparty's. Text kept verbatim equal
            to the export's two legend lines (`_SIGN_LEGEND` + `_COLUMN_LEGEND`;
            the file splits them because a PDF prose row does not wrap). Not a
            field, so no HeaderLabel.

            Only on a document whose whole period lies in the directional era:
            pre-2026-05-23 billed rows never had pnl_direction applied to
            net_amount, so on a May-2026 re-export the first sentence is false
            on real lines (dp, LR-618 round 2, Medium-5). ISO strings compare
            like dates; the constant mirrors DIRECTION_LANDED in
            statement_builder.py, which is the source of truth - same accepted
            duplication as the legend prose itself. A straddling period starts
            before the epoch, so one comparison covers all three cases. */}
        {directional && (
          <p className="col-span-2 text-[11px] text-lr-faint">
            Amount, Fee Income, Cash Pool Interest Accrual and every total are
            signed from our perspective - positive = due to Exante. P/R names the same
            direction in your terms: P = payable by you, R = receivable by you.
            B/L is our side of the trade: L = we lend to you, B = we borrow from
            you. Rate and Collateral Rate are the rates a line accrued at and
            carry no payment direction.
          </p>
        )}
      </dl>

      {blocks.length === 0 ? (
        <div className="rounded-[8px] border border-dashed border-lr-border-2 px-4 py-8 text-center text-[12.5px] text-lr-faint">
          No accruals for this period yet.
        </div>
      ) : (
        <div
          ref={listRef}
          className="relative focus:outline-none"
          style={virtualized ? { height: virtualizer.getTotalSize() } : undefined}
        >
          {sticky?.block && (
            <div className="sticky z-10 h-0" style={{ top: stickyTop }} aria-hidden="true">
              <div ref={stickyBand} className="bg-lr-surface pb-3 pt-2">
                <BlockHeading block={sticky.block} />
                {sticky.section && (
                  <div className="-mb-2 pt-2">
                    <SectionTitle
                      section={sticky.section}
                      currency={sticky.block.currency}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            style={
              virtualized
                ? {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${offset}px)`,
                  }
                : undefined
            }
          >
            {nested}
          </div>
        </div>
      )}
    </div>
  );
};

export const StatementDocumentView = React.memo(StatementDocumentViewImpl);
